import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, BookOpen, FileText, Heart, HelpCircle, FileCheck, TrendingUp } from 'lucide-react';
import { toast } from 'react-toastify';

const ResourcesManagement = () => {
    const ARCA_GREEN = '#6B8E23';
    const API_BASE = process.env.REACT_APP_API_URL;
    if (!API_BASE) {
        throw new Error('API URL not configured');
    }

    const [activeTab, setActiveTab] = useState('publications');
    const [loading, setLoading] = useState(false);

    // Publications State
    const [publications, setPublications] = useState([]);
    const [showPubModal, setShowPubModal] = useState(false);
    const [editingPub, setEditingPub] = useState(null);
    const [uploadingCover, setUploadingCover] = useState(false);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [pubForm, setPubForm] = useState({
        title: '',
        description: '',
        year: new Date().getFullYear().toString(),
        category: '',
        author: '',
        file_url: '',
        file_size: '',
        cover_image_url: '',
        status: 'Coming Soon',
        display_order: 0
    });

    // FAQs State
    const [faqs, setFaqs] = useState([]);
    const [showFaqModal, setShowFaqModal] = useState(false);
    const [editingFaq, setEditingFaq] = useState(null);
    const [faqForm, setFaqForm] = useState({
        question: '',
        answer: '',
        category: '',
        display_order: 0
    });

    // Training Materials State
    const [trainingMaterials, setTrainingMaterials] = useState([]);
    const [showTrainingModal, setShowTrainingModal] = useState(false);
    const [editingTraining, setEditingTraining] = useState(null);
    const [trainingForm, setTrainingForm] = useState({
        title: '',
        description: '',
        category: '',
        content_type: '',
        duration: '',
        file_url: '',
        file_size: '',
        thumbnail_url: '',
        status: 'Coming Soon',
        display_order: 0
    });

    // Clinical Guidelines State
    const [guidelines, setGuidelines] = useState([]);
    const [showGuidelineModal, setShowGuidelineModal] = useState(false);
    const [editingGuideline, setEditingGuideline] = useState(null);
    const [guidelineForm, setGuidelineForm] = useState({
        title: '',
        description: '',
        category: '',
        version: '',
        file_url: '',
        file_size: '',
        cover_image_url: '',
        status: 'Coming Soon',
        display_order: 0
    });

    // Policy Documents State
    const [policyDocs, setPolicyDocs] = useState([]);
    const [showPolicyDocModal, setShowPolicyDocModal] = useState(false);
    const [editingPolicyDoc, setEditingPolicyDoc] = useState(null);
    const [policyDocForm, setPolicyDocForm] = useState({
        title: '',
        description: '',
        category: '',
        effective_date: '',
        file_url: '',
        file_size: '',
        status: 'Coming Soon',
        display_order: 0
    });

    // Policy Developments State
    const [policyDevs, setPolicyDevs] = useState([]);
    const [showPolicyDevModal, setShowPolicyDevModal] = useState(false);
    const [editingPolicyDev, setEditingPolicyDev] = useState(null);
    const [policyDevForm, setPolicyDevForm] = useState({
        title: '',
        description: '',
        category: '',
        published_date: '',
        content: '',
        image_url: '',
        display_order: 0
    });

    const categoryOptions = {
        publications: ['Clinical Guidelines', 'Research Papers', 'Annual Reports', 'White Papers'],
        faqs: ['Membership', 'Training', 'Certification', 'General', 'Technical'],
        trainingMaterials: ['Clinical Training', 'Online Courses', 'Workshops', 'Webinars', 'Certification Programs'],
        guidelines: ['Ventilation', 'Oxygen Therapy', 'Airway Management', 'Pediatric Care', 'Infectious Disease', 'Emergency Care'],
        policyDocs: ['Clinical Governance', 'Professional Standards', 'Quality Assurance', 'Patient Safety', 'Ethics'],
        policyDevs: ['Professional Standards', 'Clinical Practice', 'Education & Training', 'Research & Innovation', 'International Collaboration']
    };

    useEffect(() => {
        if (activeTab === 'publications') fetchPublications();
        else if (activeTab === 'faqs') fetchFAQs();
        else if (activeTab === 'training') fetchTrainingMaterials();
        else if (activeTab === 'guidelines') fetchGuidelines();
        else if (activeTab === 'policy-docs') fetchPolicyDocs();
        else if (activeTab === 'policy-dev') fetchPolicyDevs();
    }, [activeTab]);

    // ============================================
    // PUBLICATIONS FUNCTIONS
    // ============================================

    const fetchPublications = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/resources/publications`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setPublications(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching publications:', error);
            toast.error('Failed to load publications');
        } finally {
            setLoading(false);
        }
    };

    const handleSavePublication = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('adminToken');
            const url = editingPub
                ? `${API_BASE}/admin/resources/publications/${editingPub.id}`
                : `${API_BASE}/admin/resources/publications`;

            const response = await fetch(url, {
                method: editingPub ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(pubForm)
            });

            const data = await response.json();

            if (data.success) {
                setShowPubModal(false);
                resetPubForm();
                fetchPublications();
                toast.success(`Publication ${editingPub ? 'updated' : 'created'} successfully`);
            } else {
                toast.error(data.message || 'Operation failed');
            }
        } catch (error) {
            console.error('Error saving publication:', error);
            toast.error('Failed to save publication');
        }
    };

    const handleEditPublication = (pub) => {
        setEditingPub(pub);
        setPubForm({
            title: pub.title || '',
            description: pub.description || '',
            year: pub.year || new Date().getFullYear().toString(),
            category: pub.category || '',
            author: pub.author || '',
            file_url: pub.file_url || '',
            file_size: pub.file_size || '',
            cover_image_url: pub.cover_image_url || '',
            status: pub.status || 'Coming Soon',
            display_order: pub.display_order || 0
        });
        setShowPubModal(true);
    };

    const handleDeletePublication = async (id) => {
        if (!window.confirm('Are you sure you want to delete this publication?')) return;

        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/resources/publications/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();

            if (data.success) {
                fetchPublications();
                toast.success('Publication deleted successfully');
            } else {
                toast.error(data.message || 'Failed to delete publication');
            }
        } catch (error) {
            console.error('Error deleting publication:', error);
            toast.error('Failed to delete publication');
        }
    };

    const resetPubForm = () => {
        setEditingPub(null);
        setPubForm({
            title: '',
            description: '',
            year: new Date().getFullYear().toString(),
            category: '',
            author: '',
            file_url: '',
            file_size: '',
            cover_image_url: '',
            status: 'Coming Soon',
            display_order: 0
        });
    };

    // Handle Cover Image Upload
    const handleCoverImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const validImageTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
        if (!validImageTypes.includes(file.type)) {
            toast.error('Please upload a valid image file (JPEG, PNG, WebP)');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image size must be less than 5MB');
            return;
        }

        try {
            setUploadingCover(true);
            const formData = new FormData();
            formData.append('file', file);
            formData.append('category', 'publications');

            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            const data = await response.json();

            if (data.success) {
                setPubForm({ ...pubForm, cover_image_url: data.fileUrl });
                toast.success('Cover image uploaded successfully');
            } else {
                toast.error(data.message || 'Failed to upload image');
            }
        } catch (error) {
            console.error('Error uploading cover image:', error);
            toast.error('Failed to upload cover image');
        } finally {
            setUploadingCover(false);
        }
    };

    // Handle File Upload
    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file size (max 20MB)
        if (file.size > 20 * 1024 * 1024) {
            toast.error('File size must be less than 20MB');
            return;
        }

        try {
            setUploadingFile(true);
            const formData = new FormData();
            formData.append('file', file);
            formData.append('category', 'publications');

            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            const data = await response.json();

            if (data.success) {
                // Calculate file size
                const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);
                setPubForm({
                    ...pubForm,
                    file_url: data.fileUrl,
                    file_size: `${fileSizeInMB} MB`
                });
                toast.success('File uploaded successfully');
            } else {
                toast.error(data.message || 'Failed to upload file');
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            toast.error('Failed to upload file');
        } finally {
            setUploadingFile(false);
        }
    };

    // ============================================
    // FAQs FUNCTIONS
    // ============================================

    const fetchFAQs = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/resources/faqs`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setFaqs(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching FAQs:', error);
            toast.error('Failed to load FAQs');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveFAQ = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('adminToken');
            const url = editingFaq
                ? `${API_BASE}/admin/resources/faqs/${editingFaq.id}`
                : `${API_BASE}/admin/resources/faqs`;

            const response = await fetch(url, {
                method: editingFaq ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(faqForm)
            });

            const data = await response.json();

            if (data.success) {
                setShowFaqModal(false);
                resetFaqForm();
                fetchFAQs();
                toast.success(`FAQ ${editingFaq ? 'updated' : 'created'} successfully`);
            } else {
                toast.error(data.message || 'Operation failed');
            }
        } catch (error) {
            console.error('Error saving FAQ:', error);
            toast.error('Failed to save FAQ');
        }
    };

    const handleEditFAQ = (faq) => {
        setEditingFaq(faq);
        setFaqForm({
            question: faq.question || '',
            answer: faq.answer || '',
            category: faq.category || '',
            display_order: faq.display_order || 0
        });
        setShowFaqModal(true);
    };

    const handleDeleteFAQ = async (id) => {
        if (!window.confirm('Are you sure you want to delete this FAQ?')) return;

        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/resources/faqs/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();

            if (data.success) {
                fetchFAQs();
                toast.success('FAQ deleted successfully');
            } else {
                toast.error(data.message || 'Failed to delete FAQ');
            }
        } catch (error) {
            console.error('Error deleting FAQ:', error);
            toast.error('Failed to delete FAQ');
        }
    };

    const resetFaqForm = () => {
        setEditingFaq(null);
        setFaqForm({
            question: '',
            answer: '',
            category: '',
            display_order: 0
        });
    };

    // ============================================
    // TRAINING MATERIALS CRUD FUNCTIONS
    // ============================================
    const handleSaveTraining = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('adminToken');
            const url = editingTraining
                ? `${API_BASE}/admin/resources/training-materials/${editingTraining.id}`
                : `${API_BASE}/admin/resources/training-materials`;

            const response = await fetch(url, {
                method: editingTraining ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(trainingForm)
            });

            const data = await response.json();
            if (data.success) {
                setShowTrainingModal(false);
                fetchTrainingMaterials();
                toast.success(`Training material ${editingTraining ? 'updated' : 'created'} successfully`);
            } else {
                toast.error(data.message || 'Operation failed');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to save training material');
        }
    };

    const handleEditTraining = (item) => {
        setEditingTraining(item);
        setTrainingForm({
            title: item.title || '',
            description: item.description || '',
            category: item.category || '',
            content_type: item.content_type || '',
            duration: item.duration || '',
            file_url: item.file_url || '',
            file_size: item.file_size || '',
            thumbnail_url: item.thumbnail_url || '',
            status: item.status || 'Coming Soon',
            display_order: item.display_order || 0
        });
        setShowTrainingModal(true);
    };

    const handleDeleteTraining = async (id) => {
        if (!window.confirm('Are you sure you want to delete this training material?')) return;
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/resources/training-materials/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                fetchTrainingMaterials();
                toast.success('Training material deleted successfully');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to delete training material');
        }
    };

    // ============================================
    // CLINICAL GUIDELINES CRUD FUNCTIONS
    // ============================================
    const handleSaveGuideline = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('adminToken');
            const url = editingGuideline
                ? `${API_BASE}/admin/resources/clinical-guidelines/${editingGuideline.id}`
                : `${API_BASE}/admin/resources/clinical-guidelines`;

            const response = await fetch(url, {
                method: editingGuideline ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(guidelineForm)
            });

            const data = await response.json();
            if (data.success) {
                setShowGuidelineModal(false);
                fetchGuidelines();
                toast.success(`Clinical guideline ${editingGuideline ? 'updated' : 'created'} successfully`);
            } else {
                toast.error(data.message || 'Operation failed');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to save clinical guideline');
        }
    };

    const handleEditGuideline = (item) => {
        setEditingGuideline(item);
        setGuidelineForm({
            title: item.title || '',
            description: item.description || '',
            category: item.category || '',
            version: item.version || '',
            file_url: item.file_url || '',
            file_size: item.file_size || '',
            cover_image_url: item.cover_image_url || '',
            status: item.status || 'Coming Soon',
            display_order: item.display_order || 0
        });
        setShowGuidelineModal(true);
    };

    const handleDeleteGuideline = async (id) => {
        if (!window.confirm('Are you sure you want to delete this clinical guideline?')) return;
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/resources/clinical-guidelines/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                fetchGuidelines();
                toast.success('Clinical guideline deleted successfully');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to delete clinical guideline');
        }
    };

    // ============================================
    // POLICY DOCUMENTS CRUD FUNCTIONS
    // ============================================
    const handleSavePolicyDoc = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('adminToken');
            const url = editingPolicyDoc
                ? `${API_BASE}/admin/resources/policy-documents/${editingPolicyDoc.id}`
                : `${API_BASE}/admin/resources/policy-documents`;

            const response = await fetch(url, {
                method: editingPolicyDoc ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(policyDocForm)
            });

            const data = await response.json();
            if (data.success) {
                setShowPolicyDocModal(false);
                fetchPolicyDocs();
                toast.success(`Policy document ${editingPolicyDoc ? 'updated' : 'created'} successfully`);
            } else {
                toast.error(data.message || 'Operation failed');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to save policy document');
        }
    };

    const handleEditPolicyDoc = (item) => {
        setEditingPolicyDoc(item);
        setPolicyDocForm({
            title: item.title || '',
            description: item.description || '',
            category: item.category || '',
            effective_date: item.effective_date || '',
            file_url: item.file_url || '',
            file_size: item.file_size || '',
            status: item.status || 'Coming Soon',
            display_order: item.display_order || 0
        });
        setShowPolicyDocModal(true);
    };

    const handleDeletePolicyDoc = async (id) => {
        if (!window.confirm('Are you sure you want to delete this policy document?')) return;
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/resources/policy-documents/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                fetchPolicyDocs();
                toast.success('Policy document deleted successfully');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to delete policy document');
        }
    };

    // ============================================
    // POLICY DEVELOPMENTS CRUD FUNCTIONS
    // ============================================
    const handleSavePolicyDev = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('adminToken');
            const url = editingPolicyDev
                ? `${API_BASE}/admin/resources/policy-developments/${editingPolicyDev.id}`
                : `${API_BASE}/admin/resources/policy-developments`;

            const response = await fetch(url, {
                method: editingPolicyDev ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(policyDevForm)
            });

            const data = await response.json();
            if (data.success) {
                setShowPolicyDevModal(false);
                fetchPolicyDevs();
                toast.success(`Policy development ${editingPolicyDev ? 'updated' : 'created'} successfully`);
            } else {
                toast.error(data.message || 'Operation failed');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to save policy development');
        }
    };

    const handleEditPolicyDev = (item) => {
        setEditingPolicyDev(item);
        setPolicyDevForm({
            title: item.title || '',
            description: item.description || '',
            category: item.category || '',
            published_date: item.published_date || '',
            content: item.content || '',
            image_url: item.image_url || '',
            display_order: item.display_order || 0
        });
        setShowPolicyDevModal(true);
    };

    const handleDeletePolicyDev = async (id) => {
        if (!window.confirm('Are you sure you want to delete this policy development?')) return;
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/resources/policy-developments/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                fetchPolicyDevs();
                toast.success('Policy development deleted successfully');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to delete policy development');
        }
    };

    // ============================================
    // TRAINING MATERIALS FUNCTIONS (Simplified)
    // ============================================
    const fetchTrainingMaterials = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/resources/training-materials`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) setTrainingMaterials(data.data || []);
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to load training materials');
        } finally {
            setLoading(false);
        }
    };

    // ============================================
    // CLINICAL GUIDELINES FUNCTIONS (Simplified)
    // ============================================
    const fetchGuidelines = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/resources/clinical-guidelines`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) setGuidelines(data.data || []);
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to load clinical guidelines');
        } finally {
            setLoading(false);
        }
    };

    // ============================================
    // POLICY DOCUMENTS FUNCTIONS (Simplified)
    // ============================================
    const fetchPolicyDocs = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/resources/policy-documents`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) setPolicyDocs(data.data || []);
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to load policy documents');
        } finally {
            setLoading(false);
        }
    };

    // ============================================
    // POLICY DEVELOPMENTS FUNCTIONS (Simplified)
    // ============================================
    const fetchPolicyDevs = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/resources/policy-developments`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) setPolicyDevs(data.data || []);
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to load policy developments');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="h3 mb-1" style={{ color: ARCA_GREEN }}>
                        <BookOpen size={28} className="me-2" style={{ verticalAlign: 'middle' }} />
                        Resources Management
                    </h2>
                    <p className="text-muted mb-0">Manage publications, training materials, guidelines, FAQs, and policies</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="mb-4">
                <div className="d-flex flex-wrap gap-2">
                    <button
                        type="button"
                        className={`btn ${activeTab === 'publications' ? 'btn-success' : 'btn-outline-secondary'}`}
                        onClick={() => setActiveTab('publications')}
                        style={{
                            padding: '10px 20px',
                            fontSize: '15px',
                            fontWeight: '600',
                            backgroundColor: activeTab === 'publications' ? ARCA_GREEN : '#fff',
                            borderColor: activeTab === 'publications' ? ARCA_GREEN : '#6c757d',
                            color: activeTab === 'publications' ? '#fff' : '#333'
                        }}
                    >
                        <FileText size={18} className="me-2" style={{ verticalAlign: 'middle' }} />
                        Publications
                    </button>
                    <button
                        type="button"
                        className={`btn ${activeTab === 'faqs' ? 'btn-success' : 'btn-outline-secondary'}`}
                        onClick={() => setActiveTab('faqs')}
                        style={{
                            padding: '10px 20px',
                            fontSize: '15px',
                            fontWeight: '600',
                            backgroundColor: activeTab === 'faqs' ? ARCA_GREEN : '#fff',
                            borderColor: activeTab === 'faqs' ? ARCA_GREEN : '#6c757d',
                            color: activeTab === 'faqs' ? '#fff' : '#333'
                        }}
                    >
                        <HelpCircle size={18} className="me-2" style={{ verticalAlign: 'middle' }} />
                        FAQs
                    </button>
                    <button
                        type="button"
                        className={`btn ${activeTab === 'training' ? 'btn-success' : 'btn-outline-secondary'}`}
                        onClick={() => setActiveTab('training')}
                        style={{
                            padding: '10px 20px',
                            fontSize: '15px',
                            fontWeight: '600',
                            backgroundColor: activeTab === 'training' ? ARCA_GREEN : '#fff',
                            borderColor: activeTab === 'training' ? ARCA_GREEN : '#6c757d',
                            color: activeTab === 'training' ? '#fff' : '#333'
                        }}
                    >
                        <BookOpen size={18} className="me-2" style={{ verticalAlign: 'middle' }} />
                        Training Materials
                    </button>
                    <button
                        type="button"
                        className={`btn ${activeTab === 'guidelines' ? 'btn-success' : 'btn-outline-secondary'}`}
                        onClick={() => setActiveTab('guidelines')}
                        style={{
                            padding: '10px 20px',
                            fontSize: '15px',
                            fontWeight: '600',
                            backgroundColor: activeTab === 'guidelines' ? ARCA_GREEN : '#fff',
                            borderColor: activeTab === 'guidelines' ? ARCA_GREEN : '#6c757d',
                            color: activeTab === 'guidelines' ? '#fff' : '#333'
                        }}
                    >
                        <Heart size={18} className="me-2" style={{ verticalAlign: 'middle' }} />
                        Clinical Guidelines
                    </button>
                    <button
                        type="button"
                        className={`btn ${activeTab === 'policy-docs' ? 'btn-success' : 'btn-outline-secondary'}`}
                        onClick={() => setActiveTab('policy-docs')}
                        style={{
                            padding: '10px 20px',
                            fontSize: '15px',
                            fontWeight: '600',
                            backgroundColor: activeTab === 'policy-docs' ? ARCA_GREEN : '#fff',
                            borderColor: activeTab === 'policy-docs' ? ARCA_GREEN : '#6c757d',
                            color: activeTab === 'policy-docs' ? '#fff' : '#333'
                        }}
                    >
                        <FileCheck size={18} className="me-2" style={{ verticalAlign: 'middle' }} />
                        Policy Documents
                    </button>
                    <button
                        type="button"
                        className={`btn ${activeTab === 'policy-dev' ? 'btn-success' : 'btn-outline-secondary'}`}
                        onClick={() => setActiveTab('policy-dev')}
                        style={{
                            padding: '10px 20px',
                            fontSize: '15px',
                            fontWeight: '600',
                            backgroundColor: activeTab === 'policy-dev' ? ARCA_GREEN : '#fff',
                            borderColor: activeTab === 'policy-dev' ? ARCA_GREEN : '#6c757d',
                            color: activeTab === 'policy-dev' ? '#fff' : '#333'
                        }}
                    >
                        <TrendingUp size={18} className="me-2" style={{ verticalAlign: 'middle' }} />
                        Policy Developments
                    </button>
                </div>
            </div>

            {/* Publications Tab */}
            {activeTab === 'publications' && (
                <div>
                    <div className="mb-3">
                        <button
                            className="btn text-white"
                            style={{ backgroundColor: ARCA_GREEN }}
                            onClick={() => {
                                resetPubForm();
                                setShowPubModal(true);
                            }}
                        >
                            <Plus size={18} className="me-2" />
                            Add Publication
                        </button>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" style={{ color: ARCA_GREEN }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                            <tr>
                                                <th>Order</th>
                                                <th>Title</th>
                                                <th>Category</th>
                                                <th>Year</th>
                                                <th>Author</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {publications.length === 0 ? (
                                                <tr>
                                                    <td colSpan="7" className="text-center text-muted py-4">
                                                        No publications found
                                                    </td>
                                                </tr>
                                            ) : (
                                                publications.map((pub) => (
                                                    <tr key={pub.id}>
                                                        <td>{pub.display_order}</td>
                                                        <td>
                                                            <strong>{pub.title}</strong>
                                                            <br />
                                                            <small className="text-muted">{pub.description?.substring(0, 50)}...</small>
                                                        </td>
                                                        <td><span className="badge bg-secondary">{pub.category}</span></td>
                                                        <td>{pub.year}</td>
                                                        <td><small>{pub.author}</small></td>
                                                        <td>
                                                            <span className={`badge ${pub.status === 'Available' ? 'bg-success' : 'bg-warning'}`}>
                                                                {pub.status}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary me-2"
                                                                onClick={() => handleEditPublication(pub)}
                                                                title="Edit"
                                                            >
                                                                <Edit2 size={16} />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleDeletePublication(pub.id)}
                                                                title="Delete"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* FAQs Tab */}
            {activeTab === 'faqs' && (
                <div>
                    <div className="mb-3">
                        <button
                            className="btn text-white"
                            style={{ backgroundColor: ARCA_GREEN }}
                            onClick={() => {
                                resetFaqForm();
                                setShowFaqModal(true);
                            }}
                        >
                            <Plus size={18} className="me-2" />
                            Add FAQ
                        </button>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" style={{ color: ARCA_GREEN }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                            <tr>
                                                <th>Order</th>
                                                <th>Question</th>
                                                <th>Category</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {faqs.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" className="text-center text-muted py-4">
                                                        No FAQs found
                                                    </td>
                                                </tr>
                                            ) : (
                                                faqs.map((faq) => (
                                                    <tr key={faq.id}>
                                                        <td>{faq.display_order}</td>
                                                        <td>
                                                            <strong>{faq.question}</strong>
                                                            <br />
                                                            <small className="text-muted">{faq.answer?.substring(0, 60)}...</small>
                                                        </td>
                                                        <td><span className="badge bg-info">{faq.category || 'General'}</span></td>
                                                        <td>
                                                            <span className={`badge ${faq.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                                                {faq.is_active ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary me-2"
                                                                onClick={() => handleEditFAQ(faq)}
                                                                title="Edit"
                                                            >
                                                                <Edit2 size={16} />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleDeleteFAQ(faq.id)}
                                                                title="Delete"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Training Materials Tab */}
            {activeTab === 'training' && (
                <div>
                    <div className="mb-3">
                        <button
                            className="btn text-white"
                            style={{ backgroundColor: ARCA_GREEN }}
                            onClick={() => {
                                setEditingTraining(null);
                                setTrainingForm({
                                    title: '',
                                    description: '',
                                    category: '',
                                    content_type: '',
                                    duration: '',
                                    file_url: '',
                                    file_size: '',
                                    thumbnail_url: '',
                                    status: 'Coming Soon',
                                    display_order: 0
                                });
                                setShowTrainingModal(true);
                            }}
                        >
                            <Plus size={18} className="me-2" />
                            Add Training Material
                        </button>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" style={{ color: ARCA_GREEN }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                            <tr>
                                                <th>Order</th>
                                                <th>Title</th>
                                                <th>Category</th>
                                                <th>Type</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {trainingMaterials.length === 0 ? (
                                                <tr>
                                                    <td colSpan="6" className="text-center text-muted py-4">
                                                        No training materials found. Click "Add Training Material" to create one.
                                                    </td>
                                                </tr>
                                            ) : (
                                                trainingMaterials.map((item) => (
                                                    <tr key={item.id}>
                                                        <td>{item.display_order}</td>
                                                        <td>
                                                            <strong>{item.title}</strong>
                                                            <br />
                                                            <small className="text-muted">{item.description?.substring(0, 50)}...</small>
                                                        </td>
                                                        <td><span className="badge bg-secondary">{item.category}</span></td>
                                                        <td><small>{item.content_type}</small></td>
                                                        <td>
                                                            <span className={`badge ${item.status === 'Available' ? 'bg-success' : 'bg-warning'}`}>
                                                                {item.status}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary me-2"
                                                                title="Edit"
                                                                onClick={() => handleEditTraining(item)}
                                                            >
                                                                <Edit2 size={16} />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                title="Delete"
                                                                onClick={() => handleDeleteTraining(item.id)}
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Clinical Guidelines Tab */}
            {activeTab === 'guidelines' && (
                <div>
                    <div className="mb-3">
                        <button
                            className="btn text-white"
                            style={{ backgroundColor: ARCA_GREEN }}
                            onClick={() => {
                                setEditingGuideline(null);
                                setGuidelineForm({
                                    title: '',
                                    description: '',
                                    category: '',
                                    version: '',
                                    file_url: '',
                                    file_size: '',
                                    cover_image_url: '',
                                    status: 'Coming Soon',
                                    display_order: 0
                                });
                                setShowGuidelineModal(true);
                            }}
                        >
                            <Plus size={18} className="me-2" />
                            Add Clinical Guideline
                        </button>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" style={{ color: ARCA_GREEN }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                            <tr>
                                                <th>Order</th>
                                                <th>Title</th>
                                                <th>Category</th>
                                                <th>Version</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {guidelines.length === 0 ? (
                                                <tr>
                                                    <td colSpan="6" className="text-center text-muted py-4">
                                                        No clinical guidelines found. Click "Add Clinical Guideline" to create one.
                                                    </td>
                                                </tr>
                                            ) : (
                                                guidelines.map((item) => (
                                                    <tr key={item.id}>
                                                        <td>{item.display_order}</td>
                                                        <td>
                                                            <strong>{item.title}</strong>
                                                            <br />
                                                            <small className="text-muted">{item.description?.substring(0, 50)}...</small>
                                                        </td>
                                                        <td><span className="badge bg-info">{item.category}</span></td>
                                                        <td><small>{item.version}</small></td>
                                                        <td>
                                                            <span className={`badge ${item.status === 'Available' ? 'bg-success' : 'bg-warning'}`}>
                                                                {item.status}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary me-2"
                                                                title="Edit"
                                                                onClick={() => handleEditGuideline(item)}
                                                            >
                                                                <Edit2 size={16} />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                title="Delete"
                                                                onClick={() => handleDeleteGuideline(item.id)}
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Policy Documents Tab */}
            {activeTab === 'policy-docs' && (
                <div>
                    <div className="mb-3">
                        <button
                            className="btn text-white"
                            style={{ backgroundColor: ARCA_GREEN }}
                            onClick={() => {
                                setEditingPolicyDoc(null);
                                setPolicyDocForm({
                                    title: '',
                                    description: '',
                                    category: '',
                                    effective_date: '',
                                    file_url: '',
                                    file_size: '',
                                    status: 'Coming Soon',
                                    display_order: 0
                                });
                                setShowPolicyDocModal(true);
                            }}
                        >
                            <Plus size={18} className="me-2" />
                            Add Policy Document
                        </button>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" style={{ color: ARCA_GREEN }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                            <tr>
                                                <th>Order</th>
                                                <th>Title</th>
                                                <th>Category</th>
                                                <th>Effective Date</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {policyDocs.length === 0 ? (
                                                <tr>
                                                    <td colSpan="6" className="text-center text-muted py-4">
                                                        No policy documents found. Click "Add Policy Document" to create one.
                                                    </td>
                                                </tr>
                                            ) : (
                                                policyDocs.map((item) => (
                                                    <tr key={item.id}>
                                                        <td>{item.display_order}</td>
                                                        <td>
                                                            <strong>{item.title}</strong>
                                                            <br />
                                                            <small className="text-muted">{item.description?.substring(0, 50)}...</small>
                                                        </td>
                                                        <td><span className="badge bg-primary">{item.category}</span></td>
                                                        <td><small>{item.effective_date}</small></td>
                                                        <td>
                                                            <span className={`badge ${item.status === 'Available' ? 'bg-success' : 'bg-warning'}`}>
                                                                {item.status}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary me-2"
                                                                title="Edit"
                                                                onClick={() => handleEditPolicyDoc(item)}
                                                            >
                                                                <Edit2 size={16} />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                title="Delete"
                                                                onClick={() => handleDeletePolicyDoc(item.id)}
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Policy Developments Tab */}
            {activeTab === 'policy-dev' && (
                <div>
                    <div className="mb-3">
                        <button
                            className="btn text-white"
                            style={{ backgroundColor: ARCA_GREEN }}
                            onClick={() => {
                                setEditingPolicyDev(null);
                                setPolicyDevForm({
                                    title: '',
                                    description: '',
                                    category: '',
                                    published_date: '',
                                    content: '',
                                    image_url: '',
                                    display_order: 0
                                });
                                setShowPolicyDevModal(true);
                            }}
                        >
                            <Plus size={18} className="me-2" />
                            Add Policy Development
                        </button>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" style={{ color: ARCA_GREEN }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                            <tr>
                                                <th>Order</th>
                                                <th>Title</th>
                                                <th>Category</th>
                                                <th>Published</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {policyDevs.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" className="text-center text-muted py-4">
                                                        No policy developments found. Click "Add Policy Development" to create one.
                                                    </td>
                                                </tr>
                                            ) : (
                                                policyDevs.map((item) => (
                                                    <tr key={item.id}>
                                                        <td>{item.display_order}</td>
                                                        <td>
                                                            <strong>{item.title}</strong>
                                                            <br />
                                                            <small className="text-muted">{item.description?.substring(0, 50)}...</small>
                                                        </td>
                                                        <td><span className="badge bg-success">{item.category}</span></td>
                                                        <td><small>{item.published_date}</small></td>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary me-2"
                                                                title="Edit"
                                                                onClick={() => handleEditPolicyDev(item)}
                                                            >
                                                                <Edit2 size={16} />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                title="Delete"
                                                                onClick={() => handleDeletePolicyDev(item.id)}
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Publication Modal */}
            {showPubModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header" style={{ backgroundColor: ARCA_GREEN, color: 'white' }}>
                                <h5 className="modal-title">
                                    {editingPub ? 'Edit Publication' : 'Add Publication'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => {
                                        setShowPubModal(false);
                                        resetPubForm();
                                    }}
                                ></button>
                            </div>
                            <form onSubmit={handleSavePublication}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Title *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={pubForm.title}
                                            onChange={(e) => setPubForm({ ...pubForm, title: e.target.value })}
                                            required
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Description</label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            value={pubForm.description}
                                            onChange={(e) => setPubForm({ ...pubForm, description: e.target.value })}
                                        />
                                    </div>

                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Year</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={pubForm.year}
                                                onChange={(e) => setPubForm({ ...pubForm, year: e.target.value })}
                                                placeholder="2025"
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Category</label>
                                            <select
                                                className="form-select"
                                                value={pubForm.category}
                                                onChange={(e) => setPubForm({ ...pubForm, category: e.target.value })}
                                            >
                                                <option value="">Select category</option>
                                                {categoryOptions.publications.map(cat => (
                                                    <option key={cat} value={cat}>{cat}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Status</label>
                                            <select
                                                className="form-select"
                                                value={pubForm.status}
                                                onChange={(e) => setPubForm({ ...pubForm, status: e.target.value })}
                                            >
                                                <option value="Coming Soon">Coming Soon</option>
                                                <option value="Available">Available</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Author</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={pubForm.author}
                                            onChange={(e) => setPubForm({ ...pubForm, author: e.target.value })}
                                            placeholder="e.g., ARCA Research Team"
                                        />
                                    </div>

                                    <div className="row">
                                        <div className="col-md-8 mb-3">
                                            <label className="form-label">File Upload</label>
                                            <input
                                                type="file"
                                                className="form-control"
                                                onChange={handleFileUpload}
                                                accept=".pdf,.doc,.docx,.ppt,.pptx"
                                                disabled={uploadingFile}
                                            />
                                            {uploadingFile && (
                                                <small className="text-muted">Uploading file...</small>
                                            )}
                                            {pubForm.file_url && (
                                                <small className="text-success d-block mt-1">
                                                    ✓ File uploaded: {pubForm.file_url}
                                                </small>
                                            )}
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">File Size</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={pubForm.file_size}
                                                onChange={(e) => setPubForm({ ...pubForm, file_size: e.target.value })}
                                                placeholder="Auto-filled"
                                                readOnly
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Cover Image Upload</label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            onChange={handleCoverImageUpload}
                                            accept="image/*"
                                            disabled={uploadingCover}
                                        />
                                        {uploadingCover && (
                                            <small className="text-muted">Uploading image...</small>
                                        )}
                                        {pubForm.cover_image_url && (
                                            <div className="mt-2">
                                                <small className="text-success d-block">✓ Image uploaded successfully</small>
                                                <img
                                                    src={pubForm.cover_image_url}
                                                    alt="Cover preview"
                                                    className="img-thumbnail mt-2"
                                                    style={{ maxWidth: '200px', maxHeight: '150px' }}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Display Order</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={pubForm.display_order}
                                            onChange={(e) => setPubForm({ ...pubForm, display_order: e.target.value })}
                                            placeholder="0"
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setShowPubModal(false);
                                            resetPubForm();
                                        }}
                                    >
                                        <X size={18} className="me-2" />
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn text-white" style={{ backgroundColor: ARCA_GREEN }}>
                                        <Save size={18} className="me-2" />
                                        Save Publication
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* FAQ Modal */}
            {showFaqModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header" style={{ backgroundColor: ARCA_GREEN, color: 'white' }}>
                                <h5 className="modal-title">
                                    {editingFaq ? 'Edit FAQ' : 'Add FAQ'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => {
                                        setShowFaqModal(false);
                                        resetFaqForm();
                                    }}
                                ></button>
                            </div>
                            <form onSubmit={handleSaveFAQ}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Question *</label>
                                        <textarea
                                            className="form-control"
                                            rows="2"
                                            value={faqForm.question}
                                            onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
                                            required
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Answer *</label>
                                        <textarea
                                            className="form-control"
                                            rows="4"
                                            value={faqForm.answer}
                                            onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
                                            required
                                        />
                                    </div>

                                    <div className="row">
                                        <div className="col-md-8 mb-3">
                                            <label className="form-label">Category</label>
                                            <select
                                                className="form-select"
                                                value={faqForm.category}
                                                onChange={(e) => setFaqForm({ ...faqForm, category: e.target.value })}
                                            >
                                                <option value="">Select category</option>
                                                {categoryOptions.faqs.map(cat => (
                                                    <option key={cat} value={cat}>{cat}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Display Order</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={faqForm.display_order}
                                                onChange={(e) => setFaqForm({ ...faqForm, display_order: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setShowFaqModal(false);
                                            resetFaqForm();
                                        }}
                                    >
                                        <X size={18} className="me-2" />
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn text-white" style={{ backgroundColor: ARCA_GREEN }}>
                                        <Save size={18} className="me-2" />
                                        Save FAQ
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Training Material Modal */}
            {showTrainingModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header" style={{ backgroundColor: ARCA_GREEN, color: 'white' }}>
                                <h5 className="modal-title">
                                    {editingTraining ? 'Edit Training Material' : 'Add Training Material'}
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowTrainingModal(false)}></button>
                            </div>
                            <form onSubmit={handleSaveTraining}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Title *</label>
                                        <input type="text" className="form-control" value={trainingForm.title}
                                            onChange={(e) => setTrainingForm({ ...trainingForm, title: e.target.value })} required />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Description</label>
                                        <textarea className="form-control" rows="3" value={trainingForm.description}
                                            onChange={(e) => setTrainingForm({ ...trainingForm, description: e.target.value })} />
                                    </div>
                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Category</label>
                                            <select className="form-select" value={trainingForm.category}
                                                onChange={(e) => setTrainingForm({ ...trainingForm, category: e.target.value })}>
                                                <option value="">Select category</option>
                                                {categoryOptions.trainingMaterials.map(cat => (<option key={cat} value={cat}>{cat}</option>))}
                                            </select>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Content Type</label>
                                            <input type="text" className="form-control" value={trainingForm.content_type}
                                                onChange={(e) => setTrainingForm({ ...trainingForm, content_type: e.target.value })} placeholder="e.g., PDF, Video" />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Duration</label>
                                            <input type="text" className="form-control" value={trainingForm.duration}
                                                onChange={(e) => setTrainingForm({ ...trainingForm, duration: e.target.value })} placeholder="e.g., 2 hours" />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Status</label>
                                            <select className="form-select" value={trainingForm.status}
                                                onChange={(e) => setTrainingForm({ ...trainingForm, status: e.target.value })}>
                                                <option value="Coming Soon">Coming Soon</option>
                                                <option value="Available">Available</option>
                                            </select>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Display Order</label>
                                            <input type="number" className="form-control" value={trainingForm.display_order}
                                                onChange={(e) => setTrainingForm({ ...trainingForm, display_order: parseInt(e.target.value) || 0 })} />
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowTrainingModal(false)}>Cancel</button>
                                    <button type="submit" className="btn text-white" style={{ backgroundColor: ARCA_GREEN }}>
                                        <Save size={18} className="me-2" />Save Training Material
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Clinical Guideline Modal */}
            {showGuidelineModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header" style={{ backgroundColor: ARCA_GREEN, color: 'white' }}>
                                <h5 className="modal-title">
                                    {editingGuideline ? 'Edit Clinical Guideline' : 'Add Clinical Guideline'}
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowGuidelineModal(false)}></button>
                            </div>
                            <form onSubmit={handleSaveGuideline}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Title *</label>
                                        <input type="text" className="form-control" value={guidelineForm.title}
                                            onChange={(e) => setGuidelineForm({ ...guidelineForm, title: e.target.value })} required />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Description</label>
                                        <textarea className="form-control" rows="3" value={guidelineForm.description}
                                            onChange={(e) => setGuidelineForm({ ...guidelineForm, description: e.target.value })} />
                                    </div>
                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Category</label>
                                            <select className="form-select" value={guidelineForm.category}
                                                onChange={(e) => setGuidelineForm({ ...guidelineForm, category: e.target.value })}>
                                                <option value="">Select category</option>
                                                {categoryOptions.guidelines.map(cat => (<option key={cat} value={cat}>{cat}</option>))}
                                            </select>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Version</label>
                                            <input type="text" className="form-control" value={guidelineForm.version}
                                                onChange={(e) => setGuidelineForm({ ...guidelineForm, version: e.target.value })} placeholder="e.g., 1.0" />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Status</label>
                                            <select className="form-select" value={guidelineForm.status}
                                                onChange={(e) => setGuidelineForm({ ...guidelineForm, status: e.target.value })}>
                                                <option value="Coming Soon">Coming Soon</option>
                                                <option value="Available">Available</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Display Order</label>
                                        <input type="number" className="form-control" value={guidelineForm.display_order}
                                            onChange={(e) => setGuidelineForm({ ...guidelineForm, display_order: parseInt(e.target.value) || 0 })} />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowGuidelineModal(false)}>Cancel</button>
                                    <button type="submit" className="btn text-white" style={{ backgroundColor: ARCA_GREEN }}>
                                        <Save size={18} className="me-2" />Save Clinical Guideline
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Policy Document Modal */}
            {showPolicyDocModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header" style={{ backgroundColor: ARCA_GREEN, color: 'white' }}>
                                <h5 className="modal-title">
                                    {editingPolicyDoc ? 'Edit Policy Document' : 'Add Policy Document'}
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowPolicyDocModal(false)}></button>
                            </div>
                            <form onSubmit={handleSavePolicyDoc}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Title *</label>
                                        <input type="text" className="form-control" value={policyDocForm.title}
                                            onChange={(e) => setPolicyDocForm({ ...policyDocForm, title: e.target.value })} required />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Description</label>
                                        <textarea className="form-control" rows="3" value={policyDocForm.description}
                                            onChange={(e) => setPolicyDocForm({ ...policyDocForm, description: e.target.value })} />
                                    </div>
                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Category</label>
                                            <select className="form-select" value={policyDocForm.category}
                                                onChange={(e) => setPolicyDocForm({ ...policyDocForm, category: e.target.value })}>
                                                <option value="">Select category</option>
                                                {categoryOptions.policyDocs.map(cat => (<option key={cat} value={cat}>{cat}</option>))}
                                            </select>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Effective Date</label>
                                            <input type="date" className="form-control" value={policyDocForm.effective_date}
                                                onChange={(e) => setPolicyDocForm({ ...policyDocForm, effective_date: e.target.value })} />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Status</label>
                                            <select className="form-select" value={policyDocForm.status}
                                                onChange={(e) => setPolicyDocForm({ ...policyDocForm, status: e.target.value })}>
                                                <option value="Coming Soon">Coming Soon</option>
                                                <option value="Available">Available</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Display Order</label>
                                        <input type="number" className="form-control" value={policyDocForm.display_order}
                                            onChange={(e) => setPolicyDocForm({ ...policyDocForm, display_order: parseInt(e.target.value) || 0 })} />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowPolicyDocModal(false)}>Cancel</button>
                                    <button type="submit" className="btn text-white" style={{ backgroundColor: ARCA_GREEN }}>
                                        <Save size={18} className="me-2" />Save Policy Document
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Policy Development Modal */}
            {showPolicyDevModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header" style={{ backgroundColor: ARCA_GREEN, color: 'white' }}>
                                <h5 className="modal-title">
                                    {editingPolicyDev ? 'Edit Policy Development' : 'Add Policy Development'}
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowPolicyDevModal(false)}></button>
                            </div>
                            <form onSubmit={handleSavePolicyDev}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Title *</label>
                                        <input type="text" className="form-control" value={policyDevForm.title}
                                            onChange={(e) => setPolicyDevForm({ ...policyDevForm, title: e.target.value })} required />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Description</label>
                                        <textarea className="form-control" rows="3" value={policyDevForm.description}
                                            onChange={(e) => setPolicyDevForm({ ...policyDevForm, description: e.target.value })} />
                                    </div>
                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Category</label>
                                            <select className="form-select" value={policyDevForm.category}
                                                onChange={(e) => setPolicyDevForm({ ...policyDevForm, category: e.target.value })}>
                                                <option value="">Select category</option>
                                                {categoryOptions.policyDevs.map(cat => (<option key={cat} value={cat}>{cat}</option>))}
                                            </select>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Published Date</label>
                                            <input type="date" className="form-control" value={policyDevForm.published_date}
                                                onChange={(e) => setPolicyDevForm({ ...policyDevForm, published_date: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Content</label>
                                        <textarea className="form-control" rows="3" value={policyDevForm.content}
                                            onChange={(e) => setPolicyDevForm({ ...policyDevForm, content: e.target.value })} placeholder="Detailed content of the policy development..." />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Display Order</label>
                                        <input type="number" className="form-control" value={policyDevForm.display_order}
                                            onChange={(e) => setPolicyDevForm({ ...policyDevForm, display_order: parseInt(e.target.value) || 0 })} />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowPolicyDevModal(false)}>Cancel</button>
                                    <button type="submit" className="btn text-white" style={{ backgroundColor: ARCA_GREEN }}>
                                        <Save size={18} className="me-2" />Save Policy Development
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ResourcesManagement;
